<template>
  <el-card>
    <el-form :inline="true" @submit.prevent>
      <el-form-item>
        <el-input v-model.trim="keyword" placeholder="手机号/昵称" @keyup.enter="onSearch" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="onSearch">搜索</el-button>
        <el-button @click="onReset">重置</el-button>
      </el-form-item>
    </el-form>

    <el-table :data="list" stripe border v-loading="loading">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="phone" label="手机号" min-width="140" />
      <el-table-column prop="nickname" label="昵称" min-width="120" />
      <el-table-column label="出生日期" width="130">
        <template #default="{ row }">{{ fmtDate(row.birth_date) }}</template>
      </el-table-column>
      <el-table-column label="主动联系" width="120">
        <template #default="{ row }">
          <el-tag :type="row.allow_proactive ? 'success' : 'warning'">{{ row.allow_proactive ? '已开启' : '已关闭' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="免打扰" min-width="150">
        <template #default="{ row }">{{ (row.dnd_start || '-') + ' ~ ' + (row.dnd_end || '-') }}</template>
      </el-table-column>
      <el-table-column label="创建时间" min-width="180">
        <template #default="{ row }">{{ fmt(row.created_at) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="{ row }">
          <el-space>
            <el-button link type="primary" @click="openDetail(row.id)">查看</el-button>
            <el-button link type="danger" @click="resetPassword(row.id)">重置密码</el-button>
          </el-space>
        </template>
      </el-table-column>
    </el-table>

    <div style="margin-top: 12px; display: flex; justify-content: flex-end;">
      <el-pagination
        background
        layout="total, prev, pager, next"
        :total="total"
        :current-page="query.page"
        :page-size="query.page_size"
        @current-change="onPageChange"
      />
    </div>
  </el-card>

  <el-drawer v-model="detailVisible" title="用户详情工作台" size="900px">
    <div v-loading="detailLoading">
      <template v-if="detail.user">
        <el-card shadow="never" class="profile-hero">
          <div class="hero-top">
            <div>
              <div class="hero-title">用户 #{{ detail.user.id }} · {{ detail.user.nickname || '未设置昵称' }}</div>
              <div class="hero-subtitle">{{ detail.user.phone || '-' }} · 最近登录 {{ fmt(detail.user.last_login_at) }}</div>
            </div>
            <el-space wrap>
              <el-tag :type="detail.user.allow_proactive ? 'success' : 'warning'">
                {{ detail.user.allow_proactive ? '主动联系开启' : '主动联系关闭' }}
              </el-tag>
              <el-tag type="info">IP {{ detail.user.last_login_ip || '-' }}</el-tag>
              <el-button size="small" @click="copyText(String(detail.user.id), '用户ID')">复制用户ID</el-button>
              <el-button size="small" @click="copyText(detail.user.phone || '', '手机号')">复制手机号</el-button>
              <el-button size="small" type="danger" @click="resetPassword(detail.user.id)">重置密码</el-button>
            </el-space>
          </div>
        </el-card>

        <el-row :gutter="10" style="margin-bottom: 12px;">
          <el-col :span="6"><el-statistic title="最近登录日志" :value="detail.login_logs.length" /></el-col>
          <el-col :span="6"><el-statistic title="近7天活跃天数" :value="activeDays7" /></el-col>
          <el-col :span="6"><el-statistic title="近期报告数" :value="detail.recent_reports.length" /></el-col>
          <el-col :span="6"><el-statistic title="今日额度消耗" :value="todayUsed" /></el-col>
          <el-col :span="6"><el-statistic title="历史工单数" :value="detail.tickets.length" /></el-col>
          <el-col :span="6"><el-statistic title="未关闭工单" :value="openTicketCount" /></el-col>
        </el-row>

        <el-tabs v-model="detailTab">
          <el-tab-pane label="账号与权益" name="account">
            <el-row :gutter="12">
              <el-col :span="12">
                <el-card shadow="never" class="block-card">
                  <template #header><span class="block-title">账号信息</span></template>
                  <el-descriptions :column="1" size="small" border>
                    <el-descriptions-item label="用户ID">{{ detail.user.id }}</el-descriptions-item>
                    <el-descriptions-item label="手机号">{{ detail.user.phone || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="昵称">{{ detail.user.nickname || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="出生日期">{{ fmtDate(detail.user.birth_date) }}</el-descriptions-item>
                    <el-descriptions-item label="创建时间">{{ fmt(detail.user.created_at) }}</el-descriptions-item>
                    <el-descriptions-item label="最近登录时间">{{ fmt(detail.user.last_login_at) }}</el-descriptions-item>
                    <el-descriptions-item label="最近登录IP">{{ detail.user.last_login_ip || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="免打扰">{{ (detail.user.dnd_start || '-') + ' ~ ' + (detail.user.dnd_end || '-') }}</el-descriptions-item>
                  </el-descriptions>
                </el-card>
              </el-col>
              <el-col :span="12">
                <el-card shadow="never" class="block-card">
                  <template #header><span class="block-title">会员与额度</span></template>
                  <el-descriptions :column="1" size="small" border>
                    <el-descriptions-item label="会员状态">
                      <el-tag :type="detail.subscription ? 'success' : 'info'">{{ detail.subscription ? (detail.subscription.status || 'active') : '未订阅' }}</el-tag>
                    </el-descriptions-item>
                    <el-descriptions-item label="套餐编码">{{ detail.subscription?.plan_code || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="会员开始">{{ fmt(detail.subscription?.start_at) }}</el-descriptions-item>
                    <el-descriptions-item label="会员到期">{{ fmt(detail.subscription?.end_at) }}</el-descriptions-item>
                    <el-descriptions-item label="额度日期">{{ fmt(detail.quota_today?.quota_date) }}</el-descriptions-item>
                    <el-descriptions-item label="免费消耗">{{ detail.quota_today?.free_used ?? '-' }}</el-descriptions-item>
                    <el-descriptions-item label="付费消耗">{{ detail.quota_today?.paid_used ?? '-' }}</el-descriptions-item>
                  </el-descriptions>
                </el-card>
              </el-col>
            </el-row>

            <el-card shadow="never" class="block-card">
              <template #header><span class="block-title">后台备注</span></template>
              <div class="note-content">{{ detail.user.admin_note || '-' }}</div>
            </el-card>
          </el-tab-pane>

          <el-tab-pane label="最近报告" name="reports">
            <el-table :data="detail.recent_reports || []" stripe border size="small">
              <el-table-column prop="report_id" label="报告ID" min-width="220" show-overflow-tooltip />
              <el-table-column prop="method" label="流派" width="110" />
              <el-table-column label="提问" min-width="180" show-overflow-tooltip>
                <template #default="{ row }">{{ row.question || '-' }}</template>
              </el-table-column>
              <el-table-column label="摘要" min-width="260" show-overflow-tooltip>
                <template #default="{ row }">{{ row.summary || '-' }}</template>
              </el-table-column>
              <el-table-column label="时间" min-width="170">
                <template #default="{ row }">{{ fmt(row.created_at) }}</template>
              </el-table-column>
              <el-table-column label="操作" width="110" fixed="right">
                <template #default="{ row }">
                  <el-button link type="primary" @click="copyText(row.report_id || '', '报告ID')">复制ID</el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>

          <el-tab-pane label="工单历史" name="tickets">
            <el-form :inline="true" @submit.prevent style="margin-bottom: 10px;">
              <el-form-item label="状态">
                <el-select v-model="ticketFilter.status" clearable style="width: 140px" @change="applyTicketFilter">
                  <el-option label="待处理" value="open" />
                  <el-option label="处理中" value="processing" />
                  <el-option label="已解决" value="resolved" />
                </el-select>
              </el-form-item>
              <el-form-item label="分类">
                <el-select v-model="ticketFilter.category" clearable style="width: 150px" @change="applyTicketFilter">
                  <el-option label="结果争议" value="result_dispute" />
                  <el-option label="支付问题" value="payment_issue" />
                  <el-option label="功能异常" value="bug_report" />
                  <el-option label="会员问题" value="subscription_issue" />
                </el-select>
              </el-form-item>
              <el-form-item>
                <el-button @click="resetTicketFilter">重置筛选</el-button>
              </el-form-item>
            </el-form>

            <el-table :data="filteredTickets" stripe border size="small" v-loading="ticketLoading">
              <el-table-column prop="id" label="工单ID" width="90" />
              <el-table-column prop="report_id" label="报告ID" min-width="160" show-overflow-tooltip />
              <el-table-column label="分类" width="120">
                <template #default="{ row }">{{ categoryText(row.category) }}</template>
              </el-table-column>
              <el-table-column label="优先级" width="90">
                <template #default="{ row }">{{ priorityText(row.priority) }}</template>
              </el-table-column>
              <el-table-column label="状态" width="100">
                <template #default="{ row }">
                  <el-tag :type="ticketStatusType(row.status)" size="small">{{ statusText(row.status) }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="用户催办" width="90">
                <template #default="{ row }">{{ row.user_urge_count || 0 }}</template>
              </el-table-column>
              <el-table-column label="创建时间" min-width="170">
                <template #default="{ row }">{{ fmt(row.created_at) }}</template>
              </el-table-column>
              <el-table-column label="内容" min-width="220" show-overflow-tooltip>
                <template #default="{ row }">{{ row.content || '-' }}</template>
              </el-table-column>
              <el-table-column label="处理结论" min-width="220" show-overflow-tooltip>
                <template #default="{ row }">{{ row.resolution || '-' }}</template>
              </el-table-column>
              <el-table-column label="操作" width="110" fixed="right">
                <template #default="{ row }">
                  <el-button link type="primary" @click="copyText(String(row.id), '工单ID')">复制ID</el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>

          <el-tab-pane label="登录记录" name="logins">
            <el-table :data="detail.login_logs || []" stripe border size="small">
              <el-table-column prop="id" label="ID" width="80" />
              <el-table-column prop="login_ip" label="登录IP" min-width="150" />
              <el-table-column prop="user_agent" label="设备信息" min-width="320" show-overflow-tooltip />
              <el-table-column label="登录时间" min-width="180">
                <template #default="{ row }">{{ fmt(row.login_at) }}</template>
              </el-table-column>
            </el-table>
          </el-tab-pane>
        </el-tabs>
      </template>
    </div>
  </el-drawer>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { adminApi } from '../api/admin'

const keyword = ref('')
const list = ref([])
const total = ref(0)
const loading = ref(false)
const detailVisible = ref(false)
const detailLoading = ref(false)
const detailTab = ref('account')
const detail = ref({
  user: null,
  login_logs: [],
  subscription: null,
  quota_today: null,
  recent_reports: [],
  tickets: []
})
const ticketLoading = ref(false)
const ticketFilter = ref({
  status: '',
  category: ''
})
const query = ref({
  page: 1,
  page_size: 20
})

const fmt = (v) => (v ? new Date(v).toLocaleString() : '-')
const fmtDate = (v) => {
  if (!v) return '-'
  const text = String(v)
  if (text.includes('T')) return text.slice(0, 10)
  const d = new Date(text)
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10)
  return text
}

const activeDays7 = computed(() => {
  const set = new Set()
  const now = Date.now()
  for (const item of detail.value.login_logs || []) {
    const t = new Date(item.login_at || '').getTime()
    if (!Number.isFinite(t) || now - t > 7 * 24 * 60 * 60 * 1000) continue
    set.add(new Date(t).toISOString().slice(0, 10))
  }
  return set.size
})

const todayUsed = computed(() => {
  const freeUsed = Number(detail.value.quota_today?.free_used || 0)
  const paidUsed = Number(detail.value.quota_today?.paid_used || 0)
  return freeUsed + paidUsed
})

const openTicketCount = computed(() => {
  return (detail.value.tickets || []).filter((item) => item.status !== 'resolved').length
})

const filteredTickets = computed(() => {
  const list = detail.value.tickets || []
  return list.filter((item) => {
    if (ticketFilter.value.status && item.status !== ticketFilter.value.status) return false
    if (ticketFilter.value.category && item.category !== ticketFilter.value.category) return false
    return true
  })
})

async function load() {
  loading.value = true
  try {
    const data = await adminApi.users({
      keyword: keyword.value,
      page: query.value.page,
      page_size: query.value.page_size
    })
    list.value = data.list || []
    total.value = data.total || 0
  } catch (e) {
    ElMessage.error(e.message || '加载失败')
  } finally {
    loading.value = false
  }
}

function onSearch() {
  query.value.page = 1
  load()
}

function onReset() {
  keyword.value = ''
  query.value.page = 1
  load()
}

function onPageChange(page) {
  query.value.page = page
  load()
}

async function openDetail(userID) {
  detailVisible.value = true
  detailLoading.value = true
  detailTab.value = 'account'
  detail.value = {
    user: null,
    login_logs: [],
    subscription: null,
    quota_today: null,
    recent_reports: [],
    tickets: []
  }
  ticketFilter.value.status = ''
  ticketFilter.value.category = ''
  try {
    const [baseRes, profileRes] = await Promise.allSettled([adminApi.userDetail(userID), adminApi.userProfile360(userID)])

    if (baseRes.status === 'fulfilled') {
      detail.value.user = baseRes.value?.user || null
      detail.value.login_logs = baseRes.value?.login_logs || []
    }
    if (profileRes.status === 'fulfilled') {
      detail.value.user = profileRes.value?.user || detail.value.user
      detail.value.subscription = profileRes.value?.subscription || null
      detail.value.quota_today = profileRes.value?.quota_today || null
      detail.value.recent_reports = profileRes.value?.recent_reports || []
    }
    if (!detail.value.user) {
      ElMessage.error('未获取到用户详情')
    }
    await loadUserTickets(userID)
  } catch (e) {
    ElMessage.error(e.message || '加载详情失败')
  } finally {
    detailLoading.value = false
  }
}

async function loadUserTickets(userID) {
  ticketLoading.value = true
  try {
    const res = await adminApi.ticketList({
      user_id: userID,
      page: 1,
      page_size: 50
    })
    detail.value.tickets = res?.list || []
  } catch (e) {
    detail.value.tickets = []
    ElMessage.error(e.message || '加载用户工单失败')
  } finally {
    ticketLoading.value = false
  }
}

function applyTicketFilter() {
}

function resetTicketFilter() {
  ticketFilter.value.status = ''
  ticketFilter.value.category = ''
}

function statusText(value) {
  const map = {
    open: '待处理',
    processing: '处理中',
    resolved: '已解决'
  }
  return map[value] || value || '-'
}

function categoryText(value) {
  const map = {
    result_dispute: '结果争议',
    payment_issue: '支付问题',
    bug_report: '功能异常',
    subscription_issue: '会员问题'
  }
  return map[value] || value || '-'
}

function priorityText(value) {
  const map = {
    low: '低',
    medium: '中',
    high: '高',
    urgent: '紧急'
  }
  return map[value] || value || '-'
}

function ticketStatusType(value) {
  const map = {
    open: 'warning',
    processing: '',
    resolved: 'success'
  }
  return map[value] || 'info'
}

async function resetPassword(userID) {
  try {
    await ElMessageBox.confirm(
      `确认将用户 #${userID} 的密码重置为 123456 吗？系统会自动记录后台备注。`,
      '重置密码确认',
      { type: 'warning' }
    )
    await adminApi.resetUserPassword(userID)
    ElMessage.success('密码已重置为 123456')
    if (detailVisible.value && detail.value.user && detail.value.user.id === userID) {
      await openDetail(userID)
    }
    await load()
  } catch (e) {
    if (e === 'cancel') return
    ElMessage.error(e.message || '重置失败')
  }
}

async function copyText(text, label) {
  const value = String(text || '').trim()
  if (!value) {
    ElMessage.warning(`暂无可复制${label}`)
    return
  }
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      ElMessage.success(`${label}已复制`)
      return
    }
  } catch (_) {
  }
  try {
    const input = document.createElement('textarea')
    input.value = value
    document.body.appendChild(input)
    input.select()
    document.execCommand('copy')
    document.body.removeChild(input)
    ElMessage.success(`${label}已复制`)
  } catch (_) {
    ElMessage.error('复制失败，请手动复制')
  }
}

onMounted(load)
</script>

<style scoped>
.profile-hero {
  margin-bottom: 12px;
}

.hero-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.hero-title {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
}

.hero-subtitle {
  margin-top: 6px;
  color: #6b7280;
  font-size: 12px;
}

.block-card {
  margin-bottom: 12px;
}

.block-title {
  font-weight: 700;
}

.note-content {
  color: #374151;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 13px;
}
</style>
